import React, { useState, useEffect } from 'react';
import { 
  UserCheck, 
  Search, 
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  Star,
  Calendar,
  Clock,
  Users,
  Activity,
  Download,
  RefreshCw,
  Award,
  Briefcase
} from 'lucide-react';
import { Card, Button, Badge, Input, Select, Modal } from '../../components/ui';
import AdminSidebar from '../../components/layout/AdminSidebar';
import { doctorsAPI, appointmentsAPI, realTimeUpdates } from '../../services/api';
import { formatTime } from '../../utils';
import { Doctor, Appointment } from '../../types';

const AdminDoctors: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load doctors and appointments from API
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [doctorsResult, appointmentsResult] = await Promise.all([
        doctorsAPI.getAll(),
        appointmentsAPI.getAll()
      ]);

      if (doctorsResult.success && doctorsResult.data) {
        setDoctors(doctorsResult.data);
      } else {
        setError(doctorsResult.error || 'Failed to load doctors');
      }

      if (appointmentsResult.success && appointmentsResult.data) {
        setAppointments(appointmentsResult.data);
      }
    } catch (err) {
      setError('Network error while loading data');
      console.error('Error loading doctors data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Set up real-time updates
  useEffect(() => {
    const handleDoctorsUpdate = (updatedDoctors: Doctor[]) => {
      setDoctors(updatedDoctors);
    };

    // Subscribe to real-time updates
    realTimeUpdates.subscribe('doctors', handleDoctorsUpdate, 60000); // Every minute

    return () => {
      realTimeUpdates.unsubscribe('doctors');
    };
  }, []);

  const specialties = ['all', ...Array.from(new Set(doctors.map(d => d.specialty)))];
  const specialtyOptions = specialties.map(specialty => ({
    value: specialty,
    label: specialty === 'all' ? 'All Specialties' : specialty
  }));

  const availabilityOptions = [
    { value: 'all', label: 'All Doctors' },
    { value: 'available', label: 'Available Now' },
    { value: 'unavailable', label: 'Unavailable' }
  ];

  // Filter doctors
  const filteredDoctors = doctors.filter(doctor => {
    const matchesSearch = searchTerm === '' || 
      doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doctor.specialty.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doctor.qualifications.some(q => q.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesSpecialty = specialtyFilter === 'all' || doctor.specialty === specialtyFilter;
    const matchesAvailability = availabilityFilter === 'all' || 
      (availabilityFilter === 'available' && doctor.isAvailable) ||
      (availabilityFilter === 'unavailable' && !doctor.isAvailable);
    
    return matchesSearch && matchesSpecialty && matchesAvailability;
  });

  const getDoctorAppointments = (doctorId: string) => {
    return appointments.filter(apt => apt.doctorId === doctorId);
  };

  const getDoctorStats = (doctorId: string) => {
    const doctorAppointments = getDoctorAppointments(doctorId);
    const today = new Date();
    const thisWeek = new Date(today);
    thisWeek.setDate(today.getDate() - 7);
    
    return {
      total: doctorAppointments.length,
      completed: doctorAppointments.filter(apt => apt.status === 'completed').length,
      thisWeek: doctorAppointments.filter(apt => 
        new Date(apt.date) >= thisWeek && apt.status !== 'cancelled'
      ).length,
      upcoming: doctorAppointments.filter(apt => 
        new Date(apt.date) >= today && apt.status !== 'cancelled'
      ).length
    };
  };

  const getTodaySchedule = (doctor: Doctor) => {
    const today = new Date().getDay();
    return doctor.schedule.find(s => s.dayOfWeek === today);
  };

  const handleDeleteDoctor = (doctorId: string) => {
    if (window.confirm('Are you sure you want to remove this doctor? This action cannot be undone.')) {
      setDoctors(prev => prev.filter(d => d.id !== doctorId));
    }
  };

  const handleViewDetails = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setShowDetails(true);
  };

  const toggleAvailability = (doctorId: string) => {
    setDoctors(prev => prev.map(doctor =>
      doctor.id === doctorId
        ? { ...doctor, isAvailable: !doctor.isAvailable }
        : doctor
    ));
  };

  const stats = [
    {
      title: 'Total Doctors',
      value: doctors.length,
      icon: UserCheck,
      color: 'bg-blue-500'
    },
    {
      title: 'Available Now',
      value: doctors.filter(d => d.isAvailable).length,
      icon: Activity,
      color: 'bg-green-500'
    },
    {
      title: 'Specialties',
      value: new Set(doctors.map(d => d.specialty)).size,
      icon: Award,
      color: 'bg-purple-500'
    },
    {
      title: 'Avg Rating',
      value: (doctors.reduce((sum, d) => sum + d.rating, 0) / doctors.length || 0).toFixed(1),
      icon: Star,
      color: 'bg-yellow-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AdminSidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-20'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Doctor Management</h1>
                <p className="text-gray-600">Manage medical staff and schedules</p>
              </div>
              
              <div className="flex items-center space-x-4">
                <Button variant="outline" onClick={() => setLoading(true)}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Doctor
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search doctors by name, specialty..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <Select
                      options={specialtyOptions}
                      value={specialtyFilter}
                      onChange={setSpecialtyFilter}
                      className="min-w-40"
                    />
                    <Select
                      options={availabilityOptions}
                      value={availabilityFilter}
                      onChange={setAvailabilityFilter}
                      className="min-w-32"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {filteredDoctors.length} doctors
                  </span>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-1" />
                    Export
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Doctors Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading doctors...</span>
            </div>
          ) : filteredDoctors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDoctors.map((doctor) => {
                const stats = getDoctorStats(doctor.id);
                const todaySchedule = getTodaySchedule(doctor);
                
                return (
                  <Card key={doctor.id} className="p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <img
                            src={doctor.photo}
                            alt={doctor.name}
                            className="w-16 h-16 rounded-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://via.placeholder.com/64/E5E7EB/6B7280?text=' + doctor.name.split(' ').map(n => n[0]).join('');
                            }}
                          />
                          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                            doctor.isAvailable ? 'bg-green-500' : 'bg-red-500'
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{doctor.name}</h3>
                          <p className="text-sm text-blue-600">{doctor.specialty}</p>
                          <div className="flex items-center space-x-1 mt-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span className="text-sm text-gray-600">
                              {doctor.rating} ({doctor.reviewCount} reviews)
                            </span>
                          </div>
                        </div>
                      </div>
                      <Badge variant={doctor.isAvailable ? 'success' : 'danger'}>
                        {doctor.isAvailable ? 'Available' : 'Busy'}
                      </Badge>
                    </div>

                    {/* Qualifications */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {doctor.qualifications.slice(0, 3).map((qual, index) => (
                          <Badge key={index} variant="primary" className="text-xs">
                            {qual}
                          </Badge>
                        ))}
                        {doctor.qualifications.length > 3 && (
                          <Badge variant="primary" className="text-xs">
                            +{doctor.qualifications.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Today's Schedule */}
                    {todaySchedule && todaySchedule.isWorking && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center space-x-2 text-sm">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <span className="text-blue-800">
                            Today: {formatTime(todaySchedule.startTime)} - {formatTime(todaySchedule.endTime)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-600">{stats.upcoming}</p>
                        <p className="text-xs text-gray-600">Upcoming</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">{stats.completed}</p>
                        <p className="text-xs text-gray-600">Completed</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetails(doctor)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteDoctor(doctor.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                      
                      <Button
                        size="sm"
                        variant={doctor.isAvailable ? 'outline' : 'primary'}
                        onClick={() => toggleAvailability(doctor.id)}
                      >
                        {doctor.isAvailable ? 'Mark Busy' : 'Mark Available'}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <UserCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No doctors found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || specialtyFilter !== 'all' || availabilityFilter !== 'all'
                  ? "No doctors match your current filters."
                  : "No doctors have been added yet."
                }
              </p>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Doctor
              </Button>
            </Card>
          )}
        </div>
      </div>

      {/* Doctor Details Modal */}
      {showDetails && selectedDoctor && (
        <Modal
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          title="Doctor Details"
        >
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="flex items-start space-x-4">
              <img
                src={selectedDoctor.photo}
                alt={selectedDoctor.name}
                className="w-20 h-20 rounded-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://via.placeholder.com/80/E5E7EB/6B7280?text=' + selectedDoctor.name.split(' ').map(n => n[0]).join('');
                }}
              />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">{selectedDoctor.name}</h3>
                <p className="text-blue-600 font-medium">{selectedDoctor.specialty}</p>
                <div className="flex items-center space-x-1 mt-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="text-sm text-gray-600">
                    {selectedDoctor.rating} ({selectedDoctor.reviewCount} reviews)
                  </span>
                </div>
                <Badge 
                  variant={selectedDoctor.isAvailable ? 'success' : 'danger'} 
                  className="mt-2"
                >
                  {selectedDoctor.isAvailable ? 'Available' : 'Busy'}
                </Badge>
              </div>
            </div>

            {/* Bio */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Biography</h4>
              <p className="text-gray-700">{selectedDoctor.bio}</p>
            </div>

            {/* Qualifications */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Qualifications</h4>
              <div className="flex flex-wrap gap-2">
                {selectedDoctor.qualifications.map((qual, index) => (
                  <Badge key={index} variant="primary">
                    {qual}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Schedule */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Weekly Schedule</h4>
              <div className="space-y-2">
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                  const daySchedule = selectedDoctor.schedule.find(s => s.dayOfWeek === index);
                  return (
                    <div key={day} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="font-medium text-gray-700">{day}</span>
                      <span className="text-gray-600">
                        {daySchedule?.isWorking 
                          ? `${formatTime(daySchedule.startTime)} - ${formatTime(daySchedule.endTime)}`
                          : 'Off'
                        }
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Performance Stats */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Performance Statistics</h4>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(getDoctorStats(selectedDoctor.id)).map(([key, value]) => (
                  <div key={key} className="text-center p-3 bg-gray-50 rounded">
                    <p className="text-lg font-bold text-gray-900">{value}</p>
                    <p className="text-sm text-gray-600 capitalize">
                      {key === 'thisWeek' ? 'This Week' : key}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowDetails(false)}>
                Close
              </Button>
              <Button>
                <Edit className="w-4 h-4 mr-2" />
                Edit Doctor
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminDoctors;